import { Reveal, Eyebrow } from "./Reveal";
import { CheckCircle2, HelpCircle } from "lucide-react";
import useContent from "../lib/useContent";

const IMG_TEENS =
  "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?crop=entropy&cs=srgb&fm=jpg&q=85&auto=format&fit=crop&w=1200";

const RESEARCH_QUESTIONS = [
  "How many of us actually play football every week?",
  "Who loves watching and talking about it — but never plays?",
  "Is that a free choice, or is something in the way?",
  "What would bring the sidelines back onto the pitch?",
];

const CHOICE_REASONS = [
  "They love the drama, tactics and chat more than kicking a ball",
  "Football is a social thing — sofa, snacks, mates",
  "Gaming and other hobbies took the Sunday slot",
  "They're happy as fans and never wanted to play competitively",
];

const FORCED_REASONS = [
  "Kit, club fees and travel costs pile up",
  "No decent pitch nearby — or it's always booked",
  "School pressure and homework eat the free time",
  "Feeling judged, not good enough, or the only new face",
];

const AboutResearch = () => {
  const content = useContent();

  return (
    <section id="about" className="relative py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <Eyebrow>The project</Eyebrow>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter max-w-3xl">
            One city. One big question.{" "}
            <span className="font-serif italic font-medium marker-highlight">Zero jargon.</span>
          </h2>
        </Reveal>

        <div data-testid="findings-bento-grid" className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-6">
          <Reveal className="md:col-span-5" delay={0.05}>
            <div
              data-testid="founder-note-card"
              className="h-full rounded-3xl bg-card border border-foreground/10 p-6 md:p-8 overflow-hidden relative"
            >
              <div className="rounded-2xl overflow-hidden mb-6 rotate-1 border-4 border-paper dark:border-background">
                <img src={IMG_TEENS} alt="Young people playing football at a local kickabout" className="w-full h-44 object-cover" />
              </div>
              <p className="font-serif italic text-xl sm:text-2xl leading-snug">
                "Hi! I'm 15, and I've noticed something odd about Morbi."
              </p>
              <p className="mt-4 text-muted-foreground leading-relaxed">{content.founder_note}</p>
              <p className="mt-5 font-serif italic text-lg text-pitch dark:text-turf">
                — {content.founder_name}, age 15, founder
              </p>
            </div>
          </Reveal>

          <Reveal className="md:col-span-4" delay={0.12}>
            <div className="h-full rounded-3xl bg-pitch text-cream dark:bg-card p-6 md:p-8 relative overflow-hidden">
              <div className="absolute -bottom-10 -right-10 opacity-10" aria-hidden="true">
                <HelpCircle className="h-44 w-44" />
              </div>
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-turf">Research questions</p>
              <ul className="mt-5 space-y-4">
                {RESEARCH_QUESTIONS.map((q, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span className="font-mono text-xs text-turf mt-1">0{i + 1}</span>
                    <span className="text-sm sm:text-base leading-relaxed">{q}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal className="md:col-span-3" delay={0.18}>
            <div className="h-full rounded-3xl overflow-hidden border-4 border-card dark:border-card relative min-h-[240px]">
              <img
                src="/school-pitch.jpg"
                alt="Our school football pitch"
                className="absolute inset-0 w-full h-full object-cover saturate-[1.15] contrast-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-pitch/70 via-transparent to-transparent" aria-hidden="true" />
              <p className="absolute bottom-4 left-4 right-4 text-cream text-sm font-medium">
                Our school pitch, most evenings — beautiful, and a bit lonely.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Reveal delay={0.05}>
            <div className="h-full rounded-3xl bg-flame-soft dark:bg-card border-2 border-flame/25 p-6 md:p-8">
              <div className="flex items-center gap-3">
                <span className="h-3 w-3 rounded-full bg-flame" aria-hidden="true" />
                <h3 className="font-display text-xl font-bold">Watching by choice</h3>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                Some genuinely prefer the stand to the pitch — that's a fair outcome, and I want to count it.
              </p>
              <ul className="mt-5 space-y-3">
                {CHOICE_REASONS.map((r, i) => (
                  <li key={i} className="flex gap-2.5 items-start text-sm sm:text-base">
                    <CheckCircle2 className="h-5 w-5 text-flame shrink-0 mt-0.5" /> {r}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full rounded-3xl bg-sage/40 dark:bg-card border-2 border-pitch/20 dark:border-turf/25 p-6 md:p-8">
              <div className="flex items-center gap-3">
                <span className="h-3 w-3 rounded-full bg-pitch dark:bg-turf" aria-hidden="true" />
                <h3 className="font-display text-xl font-bold">Stopped by circumstances</h3>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                Others want to play but something gets in the way — this is the part Morbi can actually fix.
              </p>
              <ul className="mt-5 space-y-3">
                {FORCED_REASONS.map((r, i) => (
                  <li key={i} className="flex gap-2.5 items-start text-sm sm:text-base">
                    <CheckCircle2 className="h-5 w-5 text-pitch dark:text-turf shrink-0 mt-0.5" /> {r}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <p className="mt-8 text-sm text-muted-foreground max-w-2xl">
            The survey on this page exists to find out which of these reasons is actually true
            around here — so the findings go to people who can do something about it.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default AboutResearch;
